import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../../components/base/TopNavigation';
import BottomNavigation from '../../components/base/BottomNavigation';

export default function CalendarPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDayDetails, setShowDayDetails] = useState(false);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: ''
  });

  const events = [
    {
      id: 1,
      title: 'عشاء العائلة',
      time: '19:00',
      date: '2024-01-15',
      day: 15,
      member: 'فاطمة',
      color: '#5B9BD5',
      description: 'عشاء عائلي في المطعم الجديد'
    },
    {
      id: 2,
      title: 'موعد طبيب الأسنان',
      time: '14:30',
      date: '2024-01-16',
      day: 16,
      member: 'أحمد',
      color: '#F4A261',
      description: 'فحص دوري للأسنان'
    },
    {
      id: 3,
      title: 'رحلة إلى الحديقة',
      time: '10:00',
      date: '2024-01-17',
      day: 17,
      member: 'سارة',
      color: '#81C784',
      description: 'نزهة عائلية في الحديقة المركزية'
    },
    {
      id: 4,
      title: 'اجتماع المدرسة',
      time: '16:00',
      date: '2024-01-12',
      day: 12,
      member: 'محمد',
      color: '#E57373',
      description: 'اجتماع أولياء الأمور'
    }
  ];

  const handleAddEvent = () => {
    if (newEvent.title && newEvent.date && newEvent.time) {
      const eventDate = new Date(`${newEvent.date}T${newEvent.time}`);
      const newEventObj = {
        id: Date.now(),
        title: newEvent.title,
        description: newEvent.description,
        date: eventDate,
        type: 'family' as const
      };
      
      setEvents([...events, newEventObj]);
      setNewEvent({ title: '', description: '', date: '', time: '' });
      setShowAddEventModal(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const getEventsForDay = (day: number) => {
    return events.filter(event => event.day === day);
  };

  const handleDayClick = (day: number) => {
    setSelectedDay(day);
    setShowDayDetails(true);
  };

  const monthNames = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];

  const weekDays = ['أحد', 'اثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopNavigation 
        title={t('calendar')}
        rightAction={
          <button className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-add-line text-lg"></i>
          </button>
        }
      />
      
      <div className="pt-16 pb-20 px-4">
        {/* Calendar Header */}
        <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1))}
              className="w-8 h-8 flex items-center justify-center"
            >
              <i className="ri-arrow-right-s-line text-xl text-gray-600"></i>
            </button>
            <h2 className="text-lg font-bold text-gray-800">
              {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()}
            </h2>
            <button 
              onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1))}
              className="w-8 h-8 flex items-center justify-center"
            >
              <i className="ri-arrow-left-s-line text-xl text-gray-600"></i>
            </button>
          </div>

          {/* Week Days */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {getDaysInMonth(selectedDate).map((day, index) => (
              <div key={index} className="aspect-square">
                {day && (
                  <button 
                    onClick={() => handleDayClick(day)}
                    className="w-full h-full flex items-center justify-center text-sm rounded-lg hover:bg-[#5B9BD5]/10 transition-colors relative"
                  >
                    <span className={`${day === new Date().getDate() ? 'bg-[#5B9BD5] text-white rounded-full w-6 h-6 flex items-center justify-center' : ''}`}>
                      {day}
                    </span>
                    {getEventsForDay(day).length > 0 && (
                      <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#F4A261] rounded-full"></div>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Today's Events */}
        <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
          <h3 className="text-lg font-bold text-gray-800 mb-4">{t('today')}</h3>
          <div className="space-y-3">
            {events.slice(0, 2).map((event) => (
              <div key={event.id} className="flex items-center p-3 bg-gray-50 rounded-xl">
                <div 
                  className="w-4 h-4 rounded-full mr-3"
                  style={{ backgroundColor: event.color }}
                />
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800">{event.title}</h4>
                  <p className="text-sm text-gray-600">{event.time} • {event.member}</p>
                </div>
                <i className="ri-arrow-left-s-line text-gray-400"></i>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="text-lg font-bold text-gray-800 mb-4">{t('upcomingEvents')}</h3>
          <div className="space-y-3">
            {events.map((event) => (
              <div key={event.id} className="flex items-center p-3 border border-gray-100 rounded-xl">
                <div 
                  className="w-4 h-4 rounded-full mr-3"
                  style={{ backgroundColor: event.color }}
                />
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800">{event.title}</h4>
                  <p className="text-sm text-gray-600">{event.time} • {event.member}</p>
                </div>
                <div className="text-xs text-gray-500">
                  {new Date(event.date).toLocaleDateString('ar-SA')}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Floating Add Button */}
        <button 
          onClick={() => setShowAddEventModal(true)}
          className="fixed bottom-24 left-4 w-14 h-14 bg-[#5B9BD5] rounded-full flex items-center justify-center shadow-lg"
        >
          <i className="ri-add-fill text-white text-xl"></i>
        </button>
      </div>

      {/* Day Details Modal */}
      {showDayDetails && selectedDay && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50">
          <div className="bg-white rounded-t-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-800">
                  {selectedDay} {monthNames[selectedDate.getMonth()]}
                </h3>
                <button 
                  onClick={() => setShowDayDetails(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100"
                >
                  <i className="ri-close-line text-gray-600"></i>
                </button>
              </div>
            </div>
            
            <div className="p-4">
              {getEventsForDay(selectedDay).length > 0 ? (
                <div className="space-y-3">
                  {getEventsForDay(selectedDay).map((event) => (
                    <div key={event.id} className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-start">
                        <div 
                          className="w-4 h-4 rounded-full mt-1 mr-3"
                          style={{ backgroundColor: event.color }}
                        />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-800 mb-1">{event.title}</h4>
                          <p className="text-sm text-gray-600 mb-2">{event.time} • {event.member}</p>
                          <p className="text-sm text-gray-500">{event.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <i className="ri-calendar-line text-4xl text-gray-300 mb-3"></i>
                  <p className="text-gray-500">لا توجد أحداث في هذا اليوم</p>
                  <button className="mt-4 px-6 py-2 bg-[#5B9BD5] text-white rounded-full text-sm">
                    إضافة حدث
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      {showAddEventModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800">إضافة حدث جديد</h3>
              <button 
                onClick={() => setShowAddEventModal(false)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
              >
                <i className="ri-close-line text-gray-600"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">عنوان الحدث</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                  placeholder="أدخل عنوان الحدث"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5B9BD5] text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الوصف</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                  placeholder="أدخل وصف الحدث"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5B9BD5] text-sm resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">التاريخ</label>
                <input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5B9BD5] text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الوقت</label>
                <input
                  type="time"
                  value={newEvent.time}
                  onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5B9BD5] text-sm"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddEventModal(false)}
                className="flex-1 py-3 px-4 border border-gray-200 rounded-xl text-gray-600 font-medium"
              >
                إلغاء
              </button>
              <button
                onClick={handleAddEvent}
                className="flex-1 py-3 px-4 bg-[#5B9BD5] text-white rounded-xl font-medium"
              >
                إضافة الحدث
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNavigation />
    </div>
  );
}
